import React, { useState, useEffect } from "react";
import { HiOutlinePencil, HiOutlineTrash, HiDownload } from "react-icons/hi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import ExcelExport from "./ExcelExport";
import { buildApiUrl } from "../../../config";
import EditPurchaseOrderModal from "./EditPurchaseOrderModal";
import { ColorRing } from "react-loader-spinner";
import { formatDate } from "../../ui/formatDate";
import { FaExternalLinkAlt } from "react-icons/fa";
import { MdOutlineOpenInNew } from "react-icons/md";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

const PurchaseOrdersTable = () => {
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const userRole = localStorage.getItem("role");

  const handleViewClick = (referenceNo) => {
    navigate(
      `/${
        userRole === "admin" ? "admin" : "sales-executive"
      }/material-inward/${referenceNo}`
    );
  };

  useEffect(() => {
    fetchPurchaseOrders();
  }, []);

  const fetchPurchaseOrders = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        buildApiUrl("/api/purchase-order/purchase-orders"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (Array.isArray(data.data)) {
        setPurchaseOrders(data.data);
      } else {
        setError("Invalid response format. Expected an array.");
      }
    } catch (error) {
      setError("An error occurred while fetching purchase orders.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredOrders = purchaseOrders.filter(
    (order) =>
      order.referenceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.fromPerson?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.toPerson?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.createdBy?.username
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDelete = async (orderId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          buildApiUrl(`/api/purchase-order/purchase-order/${orderId}`),
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          setPurchaseOrders(
            purchaseOrders.filter((order) => order._id !== orderId)
          );
          toast.success("Purchase order deleted successfully!");
          Swal.fire(
            "Deleted!",
            "Your purchase order has been deleted.",
            "success"
          );
        } else {
          toast.error("Failed to delete purchase order.");
        }
      } catch (error) {
        toast.error("An error occurred while deleting the purchase order.");
      }
    }
  };

  const handleAddOrder = () => {
    navigate(`/sales-executive/purchaseOrder`);
  };

  const handleEdit = (referenceNo) => {
    setSelectedOrder(referenceNo);
  };

  const handleCloseModal = () => {
    setSelectedOrder(null);
  };

  return (
    <div className="overflow-x-auto bg-white">
      <div className="">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search Purchase Orders..."
            className="px-2 py-1 border border-gray-300 bg-gray-50 rounded-md shadow-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-700"
          />
        </div>

        {loading ? (
          <div className="flex justify-center items-center">
            <ColorRing
              visible={true}
              height="80"
              width="80"
              ariaLabel="color-ring-loading"
              wrapperStyle={{}}
              wrapperClass="color-ring-wrapper"
              colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
            />
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-500">
            <p>{error}</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-w-[100vw] rounded-lg shadow-md max-h-[40rem]">
            <table className="min-w-full table-auto rounded-lg overflow-scroll shadow-md border-spacing-0 border-collapse">
              <thead
                className="bg-gray-700 text-white sticky top-0 z-10"
                style={{ scrollbarWidth: "thin" }}
              >
                <tr>
                  <th className="px-2 py-1 text-left font-normal tracking-wide">
                    #
                  </th>
                  <th className="px-2 py-1 text-left font-normal tracking-wide">
                    Reference No
                  </th>
                  <th className="px-2 py-1 text-left font-normal tracking-wide">
                    Created At
                  </th>
                  <th className="px-2 py-1 text-left font-normal tracking-wide">
                    To Person
                  </th>
                  <th className="px-2 py-1 text-left font-normal tracking-wide">
                    Created By
                  </th>
                  <th className="px-2 py-1 text-left font-normal tracking-wide">
                    Status
                  </th>
                  <th className="px-2 py-1 text-center font-normal tracking-wide">
                    View Grn Id
                  </th>
                  <th className="px-2 py-1 text-center font-normal tracking-wide min-w-16">
                    Actions
                  </th>
                  <th className="px-2 py-1 text-left font-normal tracking-wide">
                    Download Excel
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan={20} className="py-2 text-xs">
                      <p className="text-center">No Orders Found!</p>
                    </td>
                  </tr>
                ) : (
                  currentItems.map((order, index) => (
                    <tr
                      key={order._id}
                      className="hover:bg-gray-100 border divide-x divide-gray-200 border-b border-collapse border-gray-200 transition-colors duration-200 cursor-pointer"
                    >
                      <td className="px-2 py-0.5 text-gray-700">
                        {index + indexOfFirstItem + 1}
                      </td>
                      <td className="px-2 py-0.5 text-gray-700 uppercase">
                        {order.referenceNo}
                      </td>
                      <td className="px-2 py-0.5 text-gray-700 text-nowrap">
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="px-2 py-0.5 text-red-700 font-semibold text-nowrap">
                        {order.toPerson}
                      </td>
                      <td className="px-2 py-0.5 text-gray-700 text-nowrap">
                        {order.createdBy?.name}
                      </td>
                      <td className="px-2 py-0.5 text-gray-700">
                        <span
                          className={`px-2 py-0.5 rounded-md capitalize ${
                            order.status === "Completed"
                              ? "bg-green-100 text-green-700"
                              : "bg-orange-100 text-orange-600"
                          }`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td className="px-2 py-0.5 text-center">
                        <button
                          onClick={() => handleViewClick(order.referenceNo)}
                          className="text-blue-500 font-semibold hover:text-blue-700"
                        >
                          <MdOutlineOpenInNew size={16} />
                        </button>
                      </td>
                      <td className="px-2 py-0.5 text-center space-x-2">
                        <button
                          onClick={() => handleEdit(order.referenceNo)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <HiOutlinePencil size={16} />
                        </button>
                        {userRole === "admin" && (
                          <button
                            onClick={() => handleDelete(order._id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <HiOutlineTrash size={16} />
                          </button>
                        )}
                      </td>
                      <td className="px-2 py-0.5 text-center">
                        <ExcelExport purchaseOrderData={order} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex justify-between items-center">
          <div>
            <span className="text-xs font-medium text-gray-400">
              Showing {indexOfFirstItem + 1} to{" "}
              {Math.min(indexOfLastItem, purchaseOrders.length)} of{" "}
              {purchaseOrders.length} orders
            </span>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg disabled:bg-gray-200"
            >
              Prev
            </button>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg disabled:bg-gray-200"
            >
              Next
            </button>
          </div>
        </div>
      </div>
      {selectedOrder && (
        <EditPurchaseOrderModal
          referenceNo={selectedOrder}
          onClose={handleCloseModal}
        />
      )}
      <ToastContainer />
    </div>
  );
};

export default PurchaseOrdersTable;
